import { cn } from '@glade-ai/ui';
import React, {
  type ChangeEvent,
  type ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Label from '../shared/Label';

import s from './Input.module.scss';

export type InputOnBlurProps = {
  id?: string;
  name?: string;
  label?: string;
  compact?: boolean;
  disabled?: boolean;
  description?: string;
  value?: string | null;
  placeholder?: string;
  error?: string;
  hasError?: boolean;
  optional?: boolean;
  hint?: string;
  suffix?: string;
  prefix?: React.ReactNode;
  type?: 'number' | 'text' | 'tel';
  min?: number;
  inputMode?: 'text' | 'email' | 'search' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal';
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (value: string, event?: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  isFetching?: boolean;
  triggerOnChangeOnBlur?: boolean;
  className?: string;
  testId?: string;
};

const InputOnBlur: ForwardRefRenderFunction<HTMLInputElement, InputOnBlurProps> = (
  {
    id,
    name,
    label,
    compact,
    disabled,
    description,
    value,
    placeholder,
    error,
    hasError,
    optional,
    hint,
    suffix,
    prefix,
    type = 'text',
    min,
    inputMode,
    onChange,
    onBlur,
    onFocus,
    isFetching,
    className,
    testId,
  },
  ref,
) => {
  const [inputValue, setInputValue] = useState(value ?? '');

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>): void => {
    onChangeRef.current?.(inputValueRef.current, event);
    onBlurRef.current?.(inputValueRef.current, event);
  }, []);

  return (
    <Label
      label={label || ''}
      hideLabel={!label && !description}
      required={!optional}
      description={description}
      errorMessage={error}
      hint={hint}
      compact={compact}
      isFetching={isFetching}
    >
      <div className={s['input-field-wrapper']}>
        {prefix ? <span className={s.prefix}>{prefix}</span> : null}
        <input
          id={id}
          ref={ref}
          name={name}
          type={type}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          required={!optional}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          min={min}
          aria-invalid={hasError || !!error || undefined}
          className={cn(
            s['input-field'],
            'focus-ring',
            (hasError || !!error) && s.error,
            !!suffix && s['has-suffix'],
            className,
          )}
          inputMode={inputMode}
          data-testid={testId}
        />
        {suffix ? <span className={s.suffix}>{suffix}</span> : null}
      </div>
    </Label>
  );
};

export default forwardRef(InputOnBlur);
